import type {
  DiretoriaChartKind,
  DiretoriaDataSource,
  DiretoriaIndicator,
  DiretoriaSeriesLabels,
} from '@/data/diretoriaTypes';

interface DiretoriaIndicatorCatalogEntry {
  id: string;
  title: string;
  shortDescription: string;
  primaryKpiIndex?: number;
  chartKind: DiretoriaChartKind;
  chartTitle?: string;
  barSeriesKey?: 'valor' | 'valor2';
  seriesLabels?: DiretoriaSeriesLabels;
  tableHeaders: string[];
  footnote: string;
  dataSource: DiretoriaDataSource;
  kpiLabels: [string, string, string];
}

export const DIRETORIA_INDICATOR_CATALOG: DiretoriaIndicatorCatalogEntry[] = [
  {
    id: 'custo-moto',
    title: 'Custo por moto',
    shortDescription: 'Custo operacional por equipamento (OS e financeiro; sem custo de compra).',
    primaryKpiIndex: 0,
    chartKind: 'barHorizontal',
    chartTitle: 'Custo médio por marca / modelo',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Custo médio', valor2: 'Qtd.' },
    tableHeaders: ['Marca / modelo', 'Custo médio', 'Qtd.'],
    footnote: 'ERP FlyStart: ativo=1; exclui custo_inicial (compra). Baixas filtráveis por período.',
    dataSource: 'erp',
    kpiLabels: ['Média atual', 'Total atual', 'Motos (base)'],
  },
  {
    id: 'faturamento-bruto',
    title: 'Faturamento bruto geral',
    shortDescription: 'Receitas realizadas (pagamentos em títulos) no ano e no mês.',
    primaryKpiIndex: 0,
    chartKind: 'line',
    chartTitle: 'Evolução mensal (ano de referência)',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Faturamento' },
    tableHeaders: ['Unidade', 'Faturamento'],
    footnote:
      'PortalFly: apenas locações APROVADA; historico_pagamentos ou valorPago+ultimoPagamento; exclui is_multa e “Recebimento Multa”.',
    dataSource: 'cobranca',
    kpiLabels: ['Anual (YTD)', 'Mês atual', 'Var. vs mês ant.'],
  },
  {
    id: 'inadimplencia',
    title: 'Inadimplência',
    shortDescription: 'Títulos vencidos/parciais (PortalFly · APROVADA) com aging 1–2 / 3–6 / 7+.',
    primaryKpiIndex: 0,
    chartKind: 'barGrouped',
    chartTitle: 'Aging por faixa de atraso',
    seriesLabels: { valor: 'Qtd.', valor2: 'Saldo' },
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    footnote:
      'PortalFly / títulos: apenas statusLocacao=APROVADA; as-of dataTo; aging civil 1–2 / 3–6 / 7+; exclui multas.',
    dataSource: 'cobranca',
    kpiLabels: ['Saldo vencido', 'Títulos vencidos', 'Novos no mês'],
  },
  {
    id: 'infracoes',
    title: 'Infrações (multas)',
    shortDescription: 'Títulos de multa no PortalFly (flag ou texto), por estado.',
    primaryKpiIndex: 0,
    chartKind: 'barGrouped',
    chartTitle: 'Multas por estado',
    seriesLabels: { valor: 'Qtd.', valor2: 'Valor' },
    tableHeaders: ['Estado', 'Qtd.', 'Valor', 'Média'],
    footnote:
      'PortalFly: is_multa ou texto Multa; Pagas = FECHADO com valorPago (data = pagamento ou vencimento); média = valor ÷ qtd.',
    dataSource: 'cobranca',
    kpiLabels: ['Pagas (mês ref.)', 'Vencidas', 'Média / multa'],
  },
  {
    id: 'motos-alugadas',
    title: 'Motos alugadas',
    shortDescription: 'Frota alugada e fluxo de locações.',
    primaryKpiIndex: 0,
    chartKind: 'barGrouped',
    chartTitle: 'Entradas e saídas',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Novas', valor2: 'Canceladas' },
    tableHeaders: ['Mês', 'Entradas', 'Saídas'],
    footnote: 'ERP FlyStart: equipamentos + locacao.',
    dataSource: 'erp',
    kpiLabels: ['Alugadas (frota)', 'Novas no mês', 'Canceladas no mês'],
  },
  {
    id: 'rentabilidade-moto',
    title: 'Rentabilidade média por moto',
    shortDescription: 'Receita realizada vs custo atual por moto.',
    primaryKpiIndex: 2,
    chartKind: 'line',
    chartTitle: 'Receita vs custo (mensal)',
    seriesLabels: { valor: 'Receita', valor2: 'Custo' },
    tableHeaders: ['Componente', 'Valor'],
    footnote: 'ERP FlyStart: receita por equipamento + custo por moto.',
    dataSource: 'erp',
    kpiLabels: ['Média receita / moto', 'Média custo / moto', 'Margem'],
  },
  {
    id: 'motos-disponiveis',
    title: 'Motos disponíveis',
    shortDescription: 'Motos com status disponível por empresa e marca.',
    primaryKpiIndex: 0,
    chartKind: 'barHorizontal',
    chartTitle: 'Disponíveis por empresa · marca',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Disponíveis' },
    tableHeaders: ['Empresa', 'Marca', 'Disponíveis'],
    footnote: 'ERP FlyStart: status_equipamento=disponivel. Gráfico por empresa · marca.',
    dataSource: 'erp',
    kpiLabels: ['Disponíveis hoje', 'Top (empresa · marca)', 'Combinações'],
  },
  {
    id: 'motos-status',
    title: 'Motos por status',
    shortDescription: 'Distribuição da frota por status operacional.',
    primaryKpiIndex: 0,
    chartKind: 'pie',
    chartTitle: 'Composição da frota por status',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Qtd.', valor2: '% frota' },
    tableHeaders: ['Status', 'Qtd.', '% frota'],
    footnote: 'ERP FlyStart: ativo=1. Distribuição por status (rótulos amigáveis).',
    dataSource: 'erp',
    kpiLabels: ['Total na base', 'Maior status', 'Situações críticas'],
  },
  {
    id: 'locacao-local',
    title: 'Locação por bairro / cidade',
    shortDescription: 'Locações ativas por endereço do locatário.',
    primaryKpiIndex: 1,
    chartKind: 'barHorizontal',
    chartTitle: 'Top cidades',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Locações' },
    tableHeaders: ['Bairro', 'Cidade', 'Locações'],
    footnote: 'ERP FlyStart: locacao + pessoa_crm.',
    dataSource: 'erp',
    kpiLabels: ['Cidades', 'Top cidade', 'Contratos (mês)'],
  },
];

const SOURCE_HINT: Record<DiretoriaDataSource, string> = {
  erp: 'ERP FlyStart',
  cobranca: 'PortalFly (títulos)',
  oficina: 'Oficina',
};

export function createPlaceholderIndicator(entry: DiretoriaIndicatorCatalogEntry): DiretoriaIndicator {
  return {
    id: entry.id,
    title: entry.title,
    shortDescription: entry.shortDescription,
    primaryKpiIndex: entry.primaryKpiIndex ?? 0,
    kpis: entry.kpiLabels.map((label) => ({
      label,
      value: '—',
      hint: SOURCE_HINT[entry.dataSource],
      tone: 'neutral',
    })),
    chartKind: entry.chartKind,
    chartTitle: entry.chartTitle,
    barSeriesKey: entry.barSeriesKey,
    seriesLabels: entry.seriesLabels,
    series: [],
    pieData: [],
    tableHeaders: entry.tableHeaders,
    tableRows: [],
    footnote: entry.footnote,
    dataSource: entry.dataSource,
    hasData: false,
  };
}

export function getDiretoriaIndicatorCatalog(): DiretoriaIndicatorCatalogEntry[] {
  return DIRETORIA_INDICATOR_CATALOG;
}

export function getPlaceholderIndicators(): DiretoriaIndicator[] {
  return DIRETORIA_INDICATOR_CATALOG.map(createPlaceholderIndicator);
}
